import type {
  AppHost,
  AppHostFactory,
  AppState,
  CapabilityRequest,
} from "../host/AppHost.js";
import type { GrantedCap } from "@gelatos/protocol";
import type { AppDescriptor } from "../host/registry.js";
import { createWorkerHost } from "../host/WorkerHost.js";
import { el, setAttr, setText } from "../lib/dom.js";
import { createIcon, updateIcon } from "../render/icon.js";
import { CAPABILITY_MIME, TreeRenderer } from "../render/reconcile.js";
import type { SurfaceFactory } from "../render/surfaces.js";
import { nodeStyleSheet } from "../render/styles.js";
import type { Geometry, Layout, WindowSnapshot } from "../workspace/schema.js";
import { beginTileGesture, beginWindowGesture } from "./gestures.js";
import css from "./WindowFrame.module.css";

export type WindowCallbacks = {
  focus: () => void;
  close: () => void;
  setGeometry: (geometry: Geometry) => void;
  setTitle: (title: string) => void;
  tileDrop: (clientX: number, clientY: number) => void;
  /** The capabilities to mount this app with (§5.3). */
  capabilities: () => GrantedCap[];
  /** Route `requestCapability` to the powerbox (§5.4). */
  request: (request: CapabilityRequest) => Promise<GrantedCap | null>;
  /** An `ObjectRef` was dropped on this window: hand its object over. */
  objectDropped: (capabilityId: string) => void;
  /**
   * How a `Surface` is implemented (§11). Scoped to this window, so a surface
   * can only ever resolve a capability this window holds.
   */
  surfaces?: SurfaceFactory;
};

const INITIAL: AppState = {
  status: "starting",
  tree: null,
  title: null,
  error: null,
  firstRenderMs: null,
};

/**
 * One floating window: chrome in the light DOM, app content in a shadow root.
 *
 * The shadow boundary is not decoration. Shell chrome and app content are
 * written by different parties, and neither should be able to restyle the
 * other. Design tokens still reach the app, because custom properties inherit
 * across the boundary — that is the only thing that crosses.
 */
export class WindowFrame {
  readonly el: HTMLElement;
  readonly windowId: string;

  #record: WindowSnapshot;
  #rect: Geometry;
  #layout: Layout = "floating";
  readonly #app: AppDescriptor | undefined;
  readonly #callbacks: WindowCallbacks;

  #host: AppHost | null = null;
  readonly #createHost: AppHostFactory;
  #state: AppState = INITIAL;
  #renderer: TreeRenderer | null = null;

  readonly #parts: {
    icon: SVGSVGElement;
    title: HTMLElement;
    badge: HTMLElement;
    body: HTMLElement;
    appRoot: HTMLElement;
    treeHost: HTMLElement;
    starting: HTMLElement;
    crash: HTMLElement;
    crashMessage: HTMLElement;
    titlebar: HTMLElement;
    resize: HTMLElement;
  };

  constructor(
    record: WindowSnapshot,
    app: AppDescriptor | undefined,
    callbacks: WindowCallbacks,
    // The one place the shell names an isolation strategy. Phase 6 swaps this
    // for `createIframeHost` and nothing else in the shell changes (§4.4).
    createHost: AppHostFactory = createWorkerHost,
  ) {
    this.#createHost = createHost;
    this.windowId = record.id;
    this.#record = record;
    this.#rect = record.geometry;
    this.#app = app;
    this.#callbacks = callbacks;

    const icon = createIcon(app?.icon ?? "warning", 13, app === undefined ? "danger" : "muted");
    const title = el("span", { class: css.titleText ?? "", "data-role": "title" });
    const badge = el("span", {
      class: css.badge ?? "",
      "data-role": "first-render",
      title: "mount → first render",
      hidden: true,
    });

    const killButton = el(
      "button",
      {
        type: "button",
        class: css.titleButton ?? "",
        "data-role": "kill",
        "data-danger": "true",
        title: "Kill the app's worker",
      },
      [createIcon("warning", 13)],
    );
    const closeButton = el(
      "button",
      {
        type: "button",
        class: css.titleButton ?? "",
        "data-role": "close",
        title: "Close window",
      },
      [createIcon("close", 13)],
    );

    const titlebar = el(
      "div",
      { class: css.title ?? "", "data-role": "titlebar" },
      [icon, title, badge, app === undefined ? null : killButton, closeButton],
    );

    const starting = el("div", { class: css.placeholder ?? "", "data-role": "starting" }, [
      createIcon("app", 18, "muted"),
      `starting ${app?.name ?? "app"}…`,
    ]);

    const crashMessage = el("div", { class: css.crashMessage ?? "" });
    const restartButton = el(
      "button",
      { type: "button", class: css.crashButton ?? "", "data-role": "restart" },
      ["Restart app"],
    );
    const crash = el("div", { class: css.crash ?? "", "data-role": "crash", hidden: true }, [
      el("div", { class: css.crashHead ?? "" }, [
        createIcon("warning", 15, "danger"),
        app === undefined ? "Unknown app" : "App stopped",
      ]),
      crashMessage,
      app === undefined ? null : restartButton,
    ]);

    const appRoot = el("div", { class: css.appRoot ?? "", "data-role": "app-root" });
    const shadow = appRoot.attachShadow({ mode: "open" });
    shadow.adoptedStyleSheets = [nodeStyleSheet()];
    const treeHost = el("div", { class: "tree-root" });
    shadow.appendChild(treeHost);

    const body = el("div", { class: css.body ?? "" }, [starting, crash, appRoot]);
    const resize = el("div", {
      class: css.resize ?? "",
      "data-role": "resize",
      "aria-hidden": "true",
    });

    this.el = el(
      "div",
      {
        class: css.frame ?? "",
        "data-window": record.id,
        "data-app": record.appId,
        "data-focused": "false",
        "data-layout": "floating",
      },
      [titlebar, body, resize],
    );

    this.#parts = {
      icon,
      title,
      badge,
      body,
      appRoot,
      treeHost,
      starting,
      crash,
      crashMessage,
      titlebar,
      resize,
    };

    if (app === undefined) {
      crash.hidden = false;
      starting.hidden = true;
      setText(crashMessage, `no app registered as “${record.appId}”`);
    } else {
      this.#renderer = new TreeRenderer(
        shadow,
        treeHost,
        (key, event, payload) => this.#host?.emit(key, event, payload),
        callbacks.surfaces,
      );
      killButton.addEventListener("click", () => this.#host?.kill("worker terminated from the window menu"));
      killButton.addEventListener("pointerdown", (event) => event.stopPropagation());
      restartButton.addEventListener("click", () => this.#start());
    }

    closeButton.addEventListener("click", () => this.#callbacks.close());
    closeButton.addEventListener("pointerdown", (event) => event.stopPropagation());
    this.el.addEventListener("pointerdown", () => this.#callbacks.focus(), { capture: true });
    titlebar.addEventListener("pointerdown", (event) => this.#beginMove(event));
    resize.addEventListener("pointerdown", (event) =>
      beginWindowGesture({
        event,
        handle: resize,
        element: this.el,
        mode: "resize",
        geometry: this.#rect,
        commit: this.#callbacks.setGeometry,
      }),
    );

    // Dropping an ObjectRef on a window hands that object to its app. What
    // crosses is a capability id, never content — the shell mints a fresh port.
    this.el.addEventListener("dragover", (event) => {
      if (!event.dataTransfer?.types.includes(CAPABILITY_MIME)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "link";
      this.el.setAttribute("data-drop-target", "true");
    });
    this.el.addEventListener("dragleave", (event) => {
      if (event.target === this.el) this.el.removeAttribute("data-drop-target");
    });
    this.el.addEventListener("drop", (event) => {
      this.el.removeAttribute("data-drop-target");
      const raw = event.dataTransfer?.getData(CAPABILITY_MIME);
      if (raw === undefined || raw === "") return;
      event.preventDefault();
      try {
        const parsed: unknown = JSON.parse(raw);
        const id =
          typeof parsed === "object" && parsed !== null
            ? (parsed as { capability?: unknown }).capability
            : undefined;
        if (typeof id === "string" && id !== "") this.#callbacks.objectDropped(id);
      } catch {
        // Not ours, or malformed. Nothing to do.
      }
    });

    this.#applyRect();
    if (app !== undefined) this.#start();
  }

  update(
    record: WindowSnapshot,
    rect: Geometry,
    layout: Layout,
    focused: boolean,
    zIndex: number,
  ): void {
    this.#record = record;
    this.#rect = rect;
    this.#layout = layout;
    this.#applyRect();
    setAttr(this.el, "data-focused", String(focused));
    setAttr(this.el, "data-layout", layout);
    this.el.style.zIndex = String(zIndex);
    // A tiled window has no resize affordance at all — the layout owns its
    // geometry — so the handle leaves the DOM rather than merely hiding.
    if (layout === "tiles") this.#parts.resize.remove();
    else if (!this.#parts.resize.isConnected) this.el.appendChild(this.#parts.resize);
    setText(this.#parts.title, record.title);
    if (this.#app !== undefined) {
      updateIcon(
        this.#parts.icon,
        this.#app.icon,
        13,
        this.#state.status === "crashed" ? "danger" : "muted",
      );
    }
  }

  /** Hand the app a capability it did not ask for. */
  grant(capability: GrantedCap): void {
    this.#host?.grant(capability);
  }

  destroy(): void {
    this.#host?.dispose();
    this.#host = null;
    this.#renderer?.destroy();
    this.#renderer = null;
  }

  #applyRect(): void {
    const style = this.el.style;
    style.left = `${this.#rect.x}px`;
    style.top = `${this.#rect.y}px`;
    style.width = `${this.#rect.w}px`;
    style.height = `${this.#rect.h}px`;
  }

  #beginMove(event: PointerEvent): void {
    this.#callbacks.focus();
    if (this.#layout === "tiles") {
      beginTileGesture({
        event,
        handle: this.#parts.titlebar,
        element: this.el,
        drop: this.#callbacks.tileDrop,
      });
      return;
    }
    beginWindowGesture({
      event,
      handle: this.#parts.titlebar,
      element: this.el,
      mode: "move",
      geometry: this.#rect,
      commit: this.#callbacks.setGeometry,
    });
  }

  #start(): void {
    const app = this.#app;
    if (app === undefined) return;
    this.#host?.dispose();
    this.#renderer?.clear();
    this.#state = INITIAL;
    this.#paintState();

    const host = this.#createHost({
      window: { id: this.#record.id, appId: app.id, w: this.#rect.w, h: this.#rect.h },
      source: app.source,
      capabilities: this.#callbacks.capabilities,
      onRequest: this.#callbacks.request,
      onState: (state) => {
        this.#state = state;
        this.#paintState();
      },
      onLog: (level, msg) => {
        const line = `[${app.id}] ${msg}`;
        if (level === "error") console.error(line);
        else if (level === "warn") console.warn(line);
        else console.info(line);
      },
    });
    this.#host = host;
    host.start();
  }

  #paintState(): void {
    const state = this.#state;
    const crashed = state.status === "crashed";

    this.#parts.crash.hidden = !crashed;
    this.#parts.starting.hidden = crashed || state.tree !== null;
    this.#parts.appRoot.hidden = crashed || state.tree === null;

    if (crashed) {
      setText(this.#parts.crashMessage, state.error ?? "unknown failure");
      this.#renderer?.clear();
    } else if (state.tree !== null) {
      this.#renderer?.render(state.tree);
    }

    if (state.firstRenderMs !== null) {
      this.#parts.badge.hidden = false;
      setText(this.#parts.badge, `${state.firstRenderMs}ms`);
    } else {
      this.#parts.badge.hidden = true;
    }

    if (this.#app !== undefined) {
      updateIcon(this.#parts.icon, this.#app.icon, 13, crashed ? "danger" : "muted");
    }

    // The app names its own window (§5.2 `title`); the shell records it in the
    // workspace document, so the name survives a reload with the app not running.
    if (state.title !== null && state.title !== "" && state.title !== this.#record.title) {
      this.#callbacks.setTitle(state.title);
    }
  }
}
